/**
 * Access Page - User Access Management
 * 
 * Centralized view of all user accesses across the platform.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Search, RefreshCw, MoreVertical, Plus, Trash2,
  Users, Building2, Eye, UserPlus, UserMinus, Check, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { api } from '../../App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const roleColors = {
  PROPRIETAIRE: 'bg-violet-100 text-violet-800',
  GOUVERNANTE: 'bg-blue-100 text-blue-800',
  FEMME_DE_MENAGE: 'bg-green-100 text-green-800',
  CONCIERGERIE: 'bg-purple-100 text-purple-800',
  STAFF: 'bg-gray-100 text-gray-800'
};

const AccessPage = () => {
  const navigate = useNavigate();
  const [accesses, setAccesses] = useState([]);
  const [stats, setStats] = useState({ total_accesses: 0, unique_users: 0, unique_riads: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialogs
  const [removeDialog, setRemoveDialog] = useState({ open: false, access: null });
  const [suspendDialog, setSuspendDialog] = useState({ open: false, access: null });
  const [addDialog, setAddDialog] = useState({ open: false });
  const [addForm, setAddForm] = useState({ email: '', riad_id: '', role: 'STAFF', password: '' });
  const [riads, setRiads] = useState([]);

  useEffect(() => {
    fetchAccesses();
    fetchRiads();
  }, [roleFilter, statusFilter, pagination.page]);

  const fetchAccesses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', 50);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('is_active', statusFilter);
      if (search) params.append('search', search);

      const { data } = await api.get(`/admin/access?${params}`);
      setAccesses(data.accesses);
      setStats(data.stats);
      setPagination(data.pagination);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchRiads = async () => {
    try {
      const { data } = await api.get('/admin/riads?limit=100');
      setRiads(data.riads);
    } catch (error) {
      console.error('Error fetching riads:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(p => ({ ...p, page: 1 }));
    fetchAccesses();
  };

  const handleRemove = async () => {
    const access = removeDialog.access;
    try {
      await api.delete(`/admin/riads/${access.riad_id}/users/${access.user_id}`);
      toast.success('Accès supprimé');
      setRemoveDialog({ open: false, access: null });
      fetchAccesses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleSuspend = async () => {
    const access = suspendDialog.access;
    try {
      await api.put(`/admin/riads/${access.riad_id}/users/${access.user_id}`, {
        is_active: !access.is_active
      });
      toast.success(access.is_active ? 'Accès suspendu' : 'Accès réactivé');
      setSuspendDialog({ open: false, access: null });
      fetchAccesses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleAddAccess = async () => {
    try {
      await api.post(`/admin/riads/${addForm.riad_id}/users`, {
        email: addForm.email,
        role: addForm.role,
        password: addForm.password || undefined
      });
      toast.success('Accès ajouté');
      setAddDialog({ open: false });
      setAddForm({ email: '', riad_id: '', role: 'STAFF', password: '' });
      fetchAccesses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  // Group accesses by user for multi-riad view
  const multiRiadUsers = Object.values(
    accesses.reduce((acc, a) => {
      if (!acc[a.user_id]) {
        acc[a.user_id] = {
          user: a.user,
          accesses: []
        };
      }
      acc[a.user_id].accesses.push(a);
      return acc;
    }, {})
  ).filter(u => u.accesses.length > 1);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Gestion des Accès</h1>
          <p className="text-muted-foreground">Vue centralisée des permissions utilisateurs</p>
        </div>
        <Button onClick={() => setAddDialog({ open: true })}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un accès
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total_accesses}</p>
              <p className="text-sm text-muted-foreground">Accès totaux</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.unique_users}</p>
              <p className="text-sm text-muted-foreground">Utilisateurs uniques</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.unique_riads}</p>
              <p className="text-sm text-muted-foreground">Riads avec accès</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <form onSubmit={handleSearch} className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom, email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10"
              data-testid="access-search-input"
            />
          </div>
        </form>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[160px]" data-testid="role-filter">
            <SelectValue placeholder="Rôle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les rôles</SelectItem>
            <SelectItem value="PROPRIETAIRE">Propriétaire</SelectItem>
            <SelectItem value="GOUVERNANTE">Gouvernante</SelectItem>
            <SelectItem value="CONCIERGERIE">Conciergerie</SelectItem>
            <SelectItem value="STAFF">Staff</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="true">Actifs</SelectItem>
            <SelectItem value="false">Suspendus</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={fetchAccesses}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">Tous les accès</TabsTrigger>
          <TabsTrigger value="multi-riad">
            Multi-riads ({multiRiadUsers.length})
          </TabsTrigger>
        </TabsList>

        {/* All Accesses Tab */}
        <TabsContent value="all">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Utilisateur</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Riad</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accesses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                        Aucun accès trouvé
                      </TableCell>
                    </TableRow>
                  ) : (
                    accesses.map(access => (
                      <TableRow key={access.id} data-testid={`access-row-${access.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {access.user?.first_name} {access.user?.last_name}
                            </p>
                            <p className="text-sm text-muted-foreground">{access.user?.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {access.riad?.ownerAccount ? (
                            <span 
                              className="text-violet-600 hover:underline cursor-pointer"
                              onClick={() => navigate(`/admin/customers/${access.riad.ownerAccount.id}`)}
                            >
                              {access.riad.ownerAccount.name}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span 
                            className="text-blue-600 hover:underline cursor-pointer"
                            onClick={() => navigate(`/admin/riads/${access.riad_id}`)}
                          >
                            {access.riad?.name}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={roleColors[access.role] || 'bg-gray-100'}>
                            {access.role}
                          </Badge>
                          {access.is_owner && (
                            <Badge variant="outline" className="ml-1">Owner</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={access.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {access.is_active ? 'Actif' : 'Suspendu'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/admin/riads/${access.riad_id}?tab=users`)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Voir le riad
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setSuspendDialog({ open: true, access })}>
                                {access.is_active ? (
                                  <>
                                    <UserMinus className="w-4 h-4 mr-2" />
                                    Suspendre l'accès
                                  </>
                                ) : (
                                  <>
                                    <UserPlus className="w-4 h-4 mr-2" />
                                    Réactiver l'accès
                                  </>
                                )}
                              </DropdownMenuItem>
                              {!access.is_owner && (
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => setRemoveDialog({ open: true, access })}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Supprimer l'accès
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* Multi-Riad Users Tab */}
        <TabsContent value="multi-riad">
          <Card>
            <CardHeader>
              <CardTitle>Utilisateurs multi-riads</CardTitle>
              <CardDescription>
                Propriétaires et conciergeries avec accès à plusieurs riads
              </CardDescription>
            </CardHeader>
            <CardContent>
              {multiRiadUsers.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  Aucun utilisateur avec accès à plusieurs riads
                </p>
              ) : (
                <div className="space-y-6">
                  {multiRiadUsers.map(({ user, accesses }) => (
                    <div key={user.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="font-semibold">{user.first_name} {user.last_name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                        <Badge variant="outline">{accesses.length} riads</Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {accesses.map(a => (
                          <div 
                            key={a.id} 
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted"
                            onClick={() => navigate(`/admin/riads/${a.riad_id}`)}
                          >
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{a.riad?.name}</span>
                            </div>
                            <Badge className={roleColors[a.role] || 'bg-gray-100'} variant="secondary">
                              {a.role}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pagination */}
      {pagination.pages > 1 && activeTab === 'all' && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={pagination.page === 1}
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
          >
            Précédent
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {pagination.page} / {pagination.pages}
          </span>
          <Button
            variant="outline"
            disabled={pagination.page === pagination.pages}
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
          >
            Suivant
          </Button>
        </div>
      )}

      {/* Add Access Dialog */}
      <Dialog open={addDialog.open} onOpenChange={(open) => setAddDialog({ open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un accès</DialogTitle>
            <DialogDescription>
              Donnez accès à un utilisateur pour un riad spécifique
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Riad</Label>
              <Select 
                value={addForm.riad_id} 
                onValueChange={v => setAddForm({ ...addForm, riad_id: v })}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Sélectionner un riad" />
                </SelectTrigger>
                <SelectContent>
                  {riads.map(r => (
                    <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={addForm.email}
                onChange={e => setAddForm({ ...addForm, email: e.target.value })}
                placeholder="utilisateur@example.com"
                className="mt-2"
              />
            </div>
            <div>
              <Label>Rôle</Label>
              <Select 
                value={addForm.role} 
                onValueChange={v => setAddForm({ ...addForm, role: v })}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PROPRIETAIRE">Propriétaire</SelectItem>
                  <SelectItem value="GOUVERNANTE">Gouvernante</SelectItem>
                  <SelectItem value="CONCIERGERIE">Conciergerie</SelectItem>
                  <SelectItem value="STAFF">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mot de passe (si nouvel utilisateur)</Label>
              <Input
                type="password"
                value={addForm.password}
                onChange={e => setAddForm({ ...addForm, password: e.target.value })}
                placeholder="Laisser vide si utilisateur existant"
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog({ open: false })}>
              Annuler
            </Button>
            <Button 
              onClick={handleAddAccess}
              disabled={!addForm.email || !addForm.riad_id}
            >
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Access Dialog */}
      <AlertDialog open={removeDialog.open} onOpenChange={(open) => setRemoveDialog({ open, access: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'accès</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer l'accès de{' '}
              <strong>{removeDialog.access?.user?.email}</strong> au riad{' '}
              <strong>{removeDialog.access?.riad?.name}</strong> ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Suspend Access Dialog */}
      <AlertDialog open={suspendDialog.open} onOpenChange={(open) => setSuspendDialog({ open, access: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {suspendDialog.access?.is_active ? 'Suspendre' : 'Réactiver'} l'accès
            </AlertDialogTitle>
            <AlertDialogDescription>
              {suspendDialog.access?.is_active 
                ? `L'utilisateur ${suspendDialog.access?.user?.email} ne pourra plus accéder au riad ${suspendDialog.access?.riad?.name}.`
                : `L'utilisateur ${suspendDialog.access?.user?.email} pourra à nouveau accéder au riad ${suspendDialog.access?.riad?.name}.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleSuspend}>
              {suspendDialog.access?.is_active ? 'Suspendre' : 'Réactiver'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AccessPage;
